// Store Pinia auth : session courante + login/logout.

import { defineStore } from "pinia";
import { apiCall, ApiCallError } from "@/shared/api/client";
import type { LoginResponse, SessionInfo } from "@/shared/api/types";

interface AuthState {
  session: SessionInfo | null;
  loading: boolean;
  error: string | null;
}

export const useAuthStore = defineStore("auth", {
  state: (): AuthState => ({ session: null, loading: false, error: null }),

  getters: {
    isAuthenticated: (s) => s.session !== null && !s.session.locked,
  },

  actions: {
    async login(masterPassword: string): Promise<boolean> {
      this.loading = true;
      this.error = null;
      try {
        await apiCall<LoginResponse>("/api/auth/login", {
          method: "POST",
          body: { masterPassword },
        });
        await this.refresh();
        return true;
      } catch (err) {
        this.error = err instanceof ApiCallError ? err.message : "login failed";
        return false;
      } finally {
        this.loading = false;
      }
    },

    async refresh(): Promise<void> {
      try {
        const res = await apiCall<SessionInfo>("/api/auth/session");
        this.session = res.data;
      } catch {
        this.session = null;
      }
    },

    async logout(): Promise<void> {
      try {
        await apiCall("/api/auth/logout", { method: "POST" });
      } finally {
        this.session = null;
      }
    },

    // v2.5.2 option E : reauthentification sans logout, restaure la
    // masterKey en RAM cote back via /api/auth/unlock + cookie mr_mk.
    async unlock(masterPassword: string): Promise<boolean> {
      this.error = null;
      try {
        await apiCall("/api/auth/unlock", {
          method: "POST",
          body: { masterPassword },
        });
        return true;
      } catch (err) {
        this.error = err instanceof ApiCallError ? err.message : "unlock failed";
        return false;
      }
    },
  },
});
